<template>
	<component
		:is="rootComponent"
		:to="to || undefined"
		:type="to ? undefined : buttonType"
		class="flex justify-center items-center px-5 rounded-normal disabled:pointer-events-none disabled:bg-dj-gray/20 disabled:text-dj-gray disabled:border-none transition-colors"
		:class="{
			'bg-dj-blue text-white hover:bg-dj-blue-dark active:bg-dj-blue-dark': type == 'primary',
			'bg-dj-red text-white hover:bg-dj-red-dark active:bg-dj-red-dark': type == 'primary-red',
			'bg-dj-blue/5 text-dj-blue hover:bg-dj-blue/10 active:bg-dj-blue/10': type == 'secondary',
			'bg-dj-red/10 text-dj-red hover:bg-dj-red/20 active:bg-dj-red/20': type == 'secondary-red',
			'bg-white text-dj-black border border-dj-grey-light hover:border-dj-grey active:bg-dj-gray/20 disabled:border-none':
				type == 'tertiary',

			'min-h-60 text-sm rounded-normal': size == 'big',
			'min-h-50 text-sm rounded-normal': size == 'normal',
			'min-h-40 text-xs rounded-normal': size == 'small',
			'min-h-30 text-xs rounded-normal': size == 'xsmall',

			'w-dj-60 min-w-60 px-0 rounded-normal': square && size == 'big',
			'w-dj-50 min-w-50 px-0 rounded-normal': square && size == 'normal',
			'w-dj-40 min-w-40 px-0 rounded-normal': square && size == 'small',

			'w-dj-60 min-w-dj-60 px-0 rounded-full': circle && size == 'big',
			'w-dj-50 min-w-dj-50 px-0 rounded-full': circle && size == 'normal',
			'w-dj-40 min-w-dj-40 px-0 rounded-full': circle && size == 'small',
			'w-dj-30 min-w-dj-30 px-0 rounded-full': circle && size == 'xsmall',

			'rounded-[60px]': rounded,

			'w-full': fullwidth,

			'flex-col': subtitle,
			'animate-pulse': loading,
		}"
		v-bind="$attrs"
	>
		<!-- @slot Use this slot to set the button text -->
		<slot></slot>
		<span v-if="subtitle" class="text-xs lowercase">{{ subtitle }}</span>
	</component>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import NuxtLink from "#app/components/nuxt-link";

export default defineComponent({
	name: "BaseButton",
	setup(props) {
		// Nuxt only auto-imports NuxtLink for static tags. A string :is "'NuxtLink'"
		// never resolves, so link buttons render as inert elements.
		const rootComponent = computed(() => (props.to ? NuxtLink : "button"));
		return { rootComponent };
	},
	props: {
		type: {
			type: String,
			default: "primary",
			validator: (value: string) => {
				// valid values — empty string is used for unstyled/bare buttons
				return ["primary", "primary-red", "secondary", "secondary-red", "tertiary", ""].includes(value);
			},
		},
		buttonType: {
			type: String,
			default: "button",
			validator: (value: string) => {
				return ["button", "submit", "reset"].includes(value);
			},
		},
		size: {
			type: String,
			default: "normal",
			validator: (value: string) => {
				// valid values
				return ["normal", "big", "small", "xsmall"].includes(value);
			},
		},
		square: {
			type: Boolean,
			default: false,
		},
		circle: {
			type: Boolean,
			default: false,
		},
		fullwidth: {
			type: Boolean,
			default: false,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		subtitle: {
			type: String,
			default: "",
		},
		rounded: {
			type: Boolean,
			default: false,
		},
		to: {
			type: String,
			default: "",
		},
	},
});
</script>
